import { ProfilePermissionType } from '@/api/backend/types';
import { usePermissions } from '@/hooks/use-permissions';
import { ReactNode } from 'react';

type PermissionGuardProps = {
  /**
   * Permission required to render the children
   */
  permission?: ProfilePermissionType;

  /**
   * Array of permissions - user must have ANY of these to render children
   */
  anyPermission?: ProfilePermissionType[];

  /**
   * Array of permissions - user must have ALL of these to render children
   */
  allPermissions?: ProfilePermissionType[];

  /**
   * Content to render if user has the required permissions
   */
  children: ReactNode;

  /**
   * Optional content to render if user does not have the required permissions
   */
  fallback?: ReactNode;
};

/**
 * A component that conditionally renders its children based on the user's permissions
 */
export const PermissionGuard = ({
  permission,
  anyPermission,
  allPermissions,
  children,
  fallback = null,
}: PermissionGuardProps) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  // Check permissions based on what was provided
  const hasRequiredPermissions =
    (permission && hasPermission(permission)) ||
    (anyPermission && hasAnyPermission(anyPermission)) ||
    (allPermissions && hasAllPermissions(allPermissions));

  if (!permission && !anyPermission && !allPermissions) {
    // If no permission checks were specified, always render the children
    return <>{children}</>;
  }

  return hasRequiredPermissions ? <>{children}</> : <>{fallback}</>;
};
